/**
 * GenericPdfCitationViewer
 *
 * A lightweight wrapper that opens a dialog to display an agent-chat PDF citation
 * using the shared PdfCitationViewer component. When opened, it fetches a citation by
 * `matterId` and `citationId` and renders the PDF with selectable citation blocks.
 *
 * Features:
 * - Controlled dialog that fetches data lazily when opened
 * - Compact or standard trigger button
 * - Simple loading state while fetching citation metadata
 * - Delegates PDF rendering and citation block highlighting to PdfCitationViewer
 *
 * Example:
 * ```tsx
 * <GenericPdfCitationViewer
 *   matterId={matterId}
 *   citationId={citationId}
 *   triggerText="View source"
 * />
 * ```
 */
import React, { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useGetAgentChatCitationQuery } from '@/services/api/agentChatService';

import { CitationViewer as PdfCitationViewer } from '../agent-chat/citations/CitationViewer';

/**
 * Props for GenericPdfCitationViewer.
 *
 * - `matterId`: The matter identifier used to fetch the citation.
 * - `citationId`: The specific citation to load and display.
 * - `triggerText`: Optional label for the trigger button (default: "View source").
 * - `compactTrigger`: When true, renders a smaller, ghost-styled trigger button.
 * - `buttonClassName`: Additional class names applied to the trigger button.
 * - `dialogTitle`: Title text shown in the dialog header (default: "Source document").
 */
interface GenericPdfCitationViewerProps {
  matterId: string;
  citationId: string;
  triggerText?: string;
  compactTrigger?: boolean;
  buttonClassName?: string;
  dialogTitle?: string;
}

/**
 * Opens a dialog and displays a PDF viewer wired to agent-chat citation blocks.
 * Fetching is deferred until the dialog opens to minimize network usage.
 */
export const GenericPdfCitationViewer: React.FC<
  GenericPdfCitationViewerProps
> = ({
  matterId,
  citationId,
  triggerText = 'View source',
  compactTrigger = false,
  buttonClassName = '',
  dialogTitle = 'Source document',
}) => {
  const [open, setOpen] = useState(false);
  const { data: citation, isLoading } = useGetAgentChatCitationQuery(
    { matterId, citationId },
    { skip: !open || !citationId },
  );

  const selectedBlockId = useMemo(
    () => citation?.blocks?.[0]?.block_id || '',
    [citation],
  );
  const [activeBlockId, setActiveBlockId] = useState<string>('');

  const handleOpen = () => setOpen(true);
  const handleChange = (next: boolean) => setOpen(next);

  return (
    <>
      <Button
        size={compactTrigger ? 'sm' : 'default'}
        variant={compactTrigger ? 'ghost' : 'outline'}
        className={buttonClassName}
        onClick={handleOpen}
        aria-label="Open source viewer"
      >
        {triggerText}
      </Button>

      <Dialog open={open} onOpenChange={handleChange}>
        <DialogContent className="flex flex-col w-full max-w-5xl h-[85vh] p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
            <DialogTitle className="text-base">{dialogTitle}</DialogTitle>
          </DialogHeader>
          {isLoading || !citation ? (
            <div className="flex items-center justify-center flex-1 text-slate-500 text-sm">
              Loading source…
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-hidden">
              <PdfCitationViewer
                url={citation.url}
                citations={citation.blocks}
                selectedCitationId={activeBlockId || selectedBlockId}
                setSelectedCitationId={setActiveBlockId}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
